import { isApiError, errorMessageOf } from './apiErrors';
import type { StatusBadgeVariant } from '../components/ui/StatusBadge';
import { BOOKING_STATUSES, type BookingStatus } from '../types/VendorBookingRecord';

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  prospecting: 'Prospecting',
  shortlisted: 'Shortlisted',
  negotiating: 'Negotiating',
  booked: 'Booked',
  'contract-signed': 'Contract signed',
  cancelled: 'Cancelled',
};

export const BOOKING_STATUS_VARIANT: Record<BookingStatus, StatusBadgeVariant> = {
  prospecting: 'neutral',
  shortlisted: 'info',
  negotiating: 'warning',
  booked: 'primary',
  'contract-signed': 'success',
  cancelled: 'danger',
};

export const BOOKING_STATUS_OPTIONS = BOOKING_STATUSES.map(value => ({ value, label: BOOKING_STATUS_LABELS[value] }));

/** Mirrors the API's transition table (services/planning/vendorBookingRules.ts); the API is the one that enforces it. */
export const BOOKING_TRANSITIONS: Readonly<Record<BookingStatus, readonly BookingStatus[]>> = {
  prospecting: ['shortlisted', 'negotiating', 'booked', 'cancelled'],
  shortlisted: ['prospecting', 'negotiating', 'booked', 'cancelled'],
  negotiating: ['prospecting', 'shortlisted', 'booked', 'cancelled'],
  booked: ['negotiating', 'contract-signed', 'cancelled'],
  'contract-signed': ['booked', 'cancelled'],
  cancelled: ['prospecting'],
};

export const isBookingStatus = (v: unknown): v is BookingStatus => typeof v === 'string' && (BOOKING_STATUSES as readonly string[]).includes(v);

/** The status as shown: an unknown or missing one reads as Prospecting (what the database defaults to). */
export const statusOf = (v: string | null | undefined): BookingStatus => (isBookingStatus(v) ? v : 'prospecting');

/** The statuses a booking can move to from `status`, in the order a person would pick them. */
export const nextStatuses = (status: string | null | undefined): BookingStatus[] => [...BOOKING_TRANSITIONS[statusOf(status)]];

/** Plain wording for the API's machine codes, so a person reads a sentence, never `booking_exists`. */
const BY_CODE: Record<string, string> = {
  booking_exists: 'This quote already has a booking.',
  quote_not_eligible: 'Only an accepted quote can become a booking.',
  quote_archived: 'This quote is archived. Restore it before booking from it.',
  no_vendor: 'This quote has no vendor yet. Pick a vendor on the quote first.',
  PROJECT_CURRENCY_NOT_SET: 'This project has no currency yet. Set the project currency in Settings first.',
  invalid_transition: 'That status change is not allowed from where this booking is now.',
  confirm_blocked: 'Before marking this booking as booked, add a deposit payment, a signed contract, or a booking date.',
  contract_status_conflict: 'A booking with status Contract signed has a signed contract. Move the status back to Booked first.',
  total_from_lines: "This booking's total is the sum of its lines. Change the lines instead.",
  booking_archived: 'This booking is archived. Restore it before changing it.',
  unconverted_lines: 'Some quote lines are in another currency than the quote. Fix them before booking.',
  negative_total: 'The quote total is below zero. Fix its lines before booking.',
  money_forbidden: 'Your role cannot enter or see money figures.',
  field_not_editable: 'That field cannot be changed on a booking.',
  booking_owned_fields: 'The contract flag, status and amount of this budget line are set on its vendor booking.',
};

/** The sentence to show for a failed booking call. */
export function bookingErrorMessage(err: unknown, fallback: string): string {
  if (isApiError(err) && err.code && BY_CODE[err.code]) return BY_CODE[err.code];
  return errorMessageOf(err, fallback);
}

/** The id of the booking a `booking_exists` refusal points at, when the API sent it. */
export function existingBookingId(err: unknown): string | null {
  if (!isApiError(err) || err.code !== 'booking_exists') return null;
  const id = (err.body as { booking_id?: unknown } | null | undefined)?.booking_id;
  return typeof id === 'string' && id ? id : null;
}
